import { config } from '@/apollo/config';
import IconCartActive from '@/assets/icons/cart/shopping-cart-added.svg';
import IconAdd from '@/assets/icons/product/icon-add.svg';
import IconArrowLeft from '@/assets/icons/icon-carousel-next.svg';
import {
  CategoryListQuery,
  DraftOrderCreateMutation,
  DraftOrderLineCreateMutation,
  ProductQuery,
  ProductVariantAvailabilityEnum,
  useDefaultChannelQuery,
  useDraftOrderCreateMutation,
  useDraftOrderLineCreateMutation,
  useDraftOrderQuery,
  useUserDraftOrdersQuery,
} from '@/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import { mapEdgesToItems } from '@/helpers/maps';
import { useTranslatedData } from '@/hooks/useTranslatedData';
import { Link, useRouter } from '@/i18n/navigation';
import { useGuestStore } from '@/zustand/guestStore';
import { ordersStore } from '@/zustand/orders';
import useUserStore from '@/zustand/useUser';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, MutableRefObject, useEffect, useState, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Button } from 'rsuite';
import styles from './reccomend.module.scss';
import { toast } from 'react-toastify';
import DefaultProductPlaceholder from '@/assets/images/Yellow.png';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';

type Props = {
  brand: string | undefined;
  brandCategories: CategoryListQuery | undefined;
  similar: ProductQuery | undefined;
  loading: boolean;
  productLoading: boolean;
  onCategorySelected?: (categoryId: string | undefined) => void;
  ref: MutableRefObject<HTMLDivElement | null>;
};

const ReccomendSection: FC<Props> = ({
  brand,
  brandCategories,
  similar,
  loading,
  onCategorySelected,
  productLoading,
  ref,
}) => {
  const splideRef = useRef<any>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const router = useRouter();
  const [isInCart, setIsInCart] = useState(false);
  const t = useTranslations('Components.product.reccomend-section');
  const locale = useLocale();
  const handleCategoryClick = (category: string | undefined) => {
    setActiveCategoryId(category || null);
    onCategorySelected && onCategorySelected(category);
  };

  useEffect(() => {
    if (
      !productLoading &&
      similar?.product?.similarProductsCategories &&
      similar.product.similarProductsCategories.length > 0 &&
      activeCategoryId === null
    ) {
      const firstCategoryId =
        similar?.product?.similarProductsCategories[0]?.parent?.id;

      handleCategoryClick(firstCategoryId);
    }
  }, [productLoading]);

  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = Boolean(userData?.id);
  const orderId = useGuestStore(state => state.guestOrderId);

  const setOrders = ordersStore(e => e.setOrders);
  const { data: channelData } = useDefaultChannelQuery();
  const [openCartModal, setOpenCartModal] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );

  const goToNextSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('>');
    }
  };

  const handleSuccessOrder = (data: DraftOrderCreateMutation) => {
    if (data?.draftOrderCreate?.errors?.length === 0) {
      setOrders(data?.draftOrderCreate?.order);
      refetchDraftOrders();
    }
  };

  const handleSuccessOrderLine = (data: DraftOrderLineCreateMutation) => {
    if (data?.orderLinesCreate?.errors?.length === 0) {
      setOrders(data?.orderLinesCreate?.order);
      refetchDraftOrders();
    }
  };

  const { data: draftOrdersData, refetch: refetchDraftOrders } =
    useUserDraftOrdersQuery({
      variables: {
        userId: userData?.id,
      },
    });

  const { data: guestDraftOrdersData, refetch: refetchGuestDraftOrdersData } =
    useDraftOrderQuery({
      variables: {
        id: orderId || '',
      },
      // skip: isLoggedIn || !orderId, // Skip if user is logged in or there's no orderId
    });
  const [addProductToCart] = useDraftOrderCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrder,
  });

  const [addProductOrderLineToCart] = useDraftOrderLineCreateMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: handleSuccessOrderLine,
  });

  const handleCartOpen = () => {
    setOpenCartModal(true);
  };

  const isProductInCart = (variantId: string) => {
    return draftOrdersData?.userDraftOrders?.[0]?.lines?.some(
      (line: any) => line?.productVariantId === variantId
    );
  };

  const addToCart = async (
    variantId: any,
    availability: ProductVariantAvailabilityEnum | null | undefined
  ) => {
    if (!variantId) {
      console.error('Invalid variant ID');
      return;
    }
    if (availability === 'OUT_OF_STOCK') {
      toast.error(`Товара нет в наличии.`);
      return;
    }
    if (availability === 'COMING_SOON') {
      toast.info(`Товар скоро появится.`);
      return;
    }
    if (isLoggedIn) {
      try {
        setLoadingStates(prev => ({ ...prev, [variantId]: true }));

        if (draftOrdersData?.userDraftOrders?.length === 0) {
          const result = await addProductToCart({
            variables: {
              input: {
                channelId: channelData?.channel?.id,
                user: userData?.id,
                lines: [
                  {
                    variantId,
                    quantity: 1,
                  },
                ],
              },
            },
          });
          if (result.data?.draftOrderCreate?.order) {
            setIsInCart(true);
          } else {
            console.error(
              'Ошибка при создании заказа:',
              result.data?.draftOrderCreate?.errors
            );
          }
        } else if (draftOrdersData?.userDraftOrders?.[0]?.isFastBuy === false) {
          const result = await addProductOrderLineToCart({
            variables: {
              id: draftOrdersData?.userDraftOrders?.[0]?.id,
              input: [
                {
                  variantId,
                  quantity: 1,
                },
              ],
            },
          });
          if (result.data?.orderLinesCreate?.order) {
            setIsInCart(true);
          } else {
            console.error(
              'Ошибка при добавлении товара в существующий черновик заказа:',
              result.data?.orderLinesCreate?.errors
            );
          }
        }

        await refetchDraftOrders();
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        setLoadingStates(prev => ({ ...prev, [variantId]: false }));
      }
    } else {
      if (!orderId) {
        try {
          const orderGuestData = await addProductToCart({
            variables: {
              input: {
                lines: [
                  {
                    variantId,
                    quantity: 1,
                  },
                ],
                isFastBuy: false,
                channelId: channelData?.channel?.id,
              },
            },
          });
          if (orderGuestData.data?.draftOrderCreate?.order) {
            setIsInCart(true);
          } else {
            console.error(
              'Ошибка при создании заказа:',
              orderGuestData.data?.draftOrderCreate?.errors
            );
          }
          const guestOrderId =
            orderGuestData?.data?.draftOrderCreate?.order?.id;
          typeof guestOrderId === 'string' &&
            useGuestStore.getState().setGuestOrderId(guestOrderId);

          await refetchGuestDraftOrdersData();
        } catch (error) {
          console.error('Ошибка при выполнении createGuestDraftOrder:', error);
        } finally {
          setLoadingStates(prev => ({ ...prev, [variantId]: false }));
        }
      } else {
        setLoadingStates(prev => ({ ...prev, [variantId]: true }));

        try {
          const result = await addProductOrderLineToCart({
            variables: {
              id: orderId,
              input: [
                {
                  variantId,
                  quantity: 1,
                },
              ],
            },
          });
          if (result.data?.orderLinesCreate?.order) {
            setIsInCart(true);
          } else {
            console.error(
              'Ошибка при добавлении товара в существующий черновик заказа:',
              result.data?.orderLinesCreate?.errors
            );
          }
          await refetchGuestDraftOrdersData();
          handleCartOpen();
        } catch (error) {
          console.error(
            'Ошибка при выполнении createGuestDraftOrderLine:',
            error
          );
        } finally {
          setLoadingStates(prev => ({ ...prev, [variantId]: false }));
        }
      }
    }
  };
  const mappedBrandData = mapEdgesToItems(brandCategories?.categories);
  const trasnlatedBrandCategories = useTranslatedData(mappedBrandData, [
    'name',
  ]);
  const edges = similar?.product?.similarProducts?.edges;
  const hasSimilarProducts = Array.isArray(edges) && edges.length > 0;
  const makeUnique = new Set();
  const uniqueCategories = similar?.product?.similarProductsCategories?.filter(
    category => {
      const parentId = category?.parent?.id;
      if (makeUnique.has(parentId)) {
        return false; // Skip duplicates
      }
      makeUnique.add(parentId);
      return true; // Keep unique categories
    }
  );

  return (
    <div className={styles.reccomendWrapper}>
      <div className={styles.contentsContainer}>
        <div className={styles.recommendedProducts}>
          {productLoading ? (
            <div className={styles.tabsSkeletons}>
              {Array.from({ length: 7 }).map((_, index) => (
                <Skeleton
                  key={`skeleton-tab-${index}`}
                  width={120}
                  height={36}
                  className={styles.skeleton}
                />
              ))}
            </div>
          ) : (
            hasSimilarProducts && (
              <>
                <div className={styles.tabsTop}>
                  <h4 className={styles.title}>{t('buys-with-product')}</h4>
                  <button
                    type="button"
                    aria-label="Next slide"
                    className={styles.tabsNextButton}
                    onClick={goToNextSlide}
                  >
                    <IconArrowLeft />
                  </button>
                </div>
                <div className={styles.tabs}>
                  <Splide
                    ref={splideRef}
                    options={{
                      rewind: true,
                      updateOnMove: true,
                      fixedWidth: 'auto',
                      perPage: 3,
                      perMove: 1,
                      arrows: false,
                      pagination: false,
                      gap: '8px',
                      padding: { right: '40px' },
                      breakpoints: {
                        1024: {
                          perPage: 3,
                          gap: '8px',
                        },
                        768: {
                          perPage: 3,
                          gap: '8px',
                        },
                        480: {
                          perPage: 1,
                          gap: '8px',
                        },
                      },
                    }}
                    hasTrack={false}
                    style={{ padding: '0' }}
                  >
                    <SplideTrack>
                      {uniqueCategories?.map(category => (
                        <SplideSlide key={category?.id}>
                          {category?.parent?.level === 1 && (
                            <button
                              type="button"
                              className={`${styles.tab} ${
                                activeCategoryId === category?.parent?.id
                                  ? styles.tabActive
                                  : ''
                              }`}
                              onClick={() =>
                                handleCategoryClick(category?.parent?.id)
                              }
                            >
                              {locale === 'ru'
                                ? category?.parent?.name
                                : category?.translation?.name}
                            </button>
                          )}
                        </SplideSlide>
                      ))}
                    </SplideTrack>
                  </Splide>
                </div>
              </>
            )
          )}
          <div className={styles.wrapperCards}>
            {productLoading
              ? Array.from({ length: 4 }, (_, index) => (
                  <div key={`skeleton-${index}`} className={styles.smallCard}>
                    <div className={styles.productPhoto}>
                      <Skeleton
                        width={64}
                        height={64}
                        className={styles.skeletonInCard}
                      />
                    </div>
                    <div className={styles.body}>
                      <div className={styles.productName}>
                        <Skeleton
                          width="80%"
                          className={styles.skeletonInCard}
                        />
                        <Skeleton
                          width="50%"
                          className={styles.skeletonInCard}
                        />
                      </div>
                      <Skeleton width="100px" className={styles.skeleton} />
                    </div>
                  </div>
                ))
              : similar?.product?.similarProducts?.edges?.map(product => {
                  const productNode = product?.node;
                  const variant = productNode?.variants?.[0];
                  const media = variant?.media?.[0];
                  const categorySlug = similar?.product?.category?.slug;
                  const installmentPrice =
                    variant?.pricing?.installmentPrice12?.amount;
                  const netPrice = variant?.pricing?.price?.net?.amount;
                  const variantId = variant?.id;
                  const isVariantExist = isLoggedIn
                    ? draftOrdersData?.userDraftOrders?.[0]?.lines?.some(
                        (line: any) => line?.productVariantId === variantId
                      )
                    : guestDraftOrdersData?.draftOrder?.lines?.some(
                        (line: any) => line?.productVariantId === variantId
                      );
                  return (
                    <Link
                      key={variantId}
                      href={
                        variant?.id
                          ? `/product/${decodeBase64Id(String(variant?.id))}-${categorySlug}`
                          : '#'
                      }
                    >
                      <div key={productNode.id} className={styles.smallCard}>
                        <div className={styles.productPhoto}>
                          <Image
                            className={styles.image146347Icon}
                            alt={media?.alt || 'Product Image'}
                            src={
                              media?.url ? media.url : DefaultProductPlaceholder
                            }
                            width={64}
                            height={64}
                          />
                        </div>
                        <div className={styles.body}>
                          <div className={styles.productName}>
                            {locale === 'ru'
                              ? variant?.name
                              : variant?.translation?.name}
                          </div>
                          {installmentPrice !== undefined &&
                            installmentPrice !== null &&
                            installmentPrice !== 0 && (
                              <div className={styles.tag}>
                                <div className={styles.logo}>
                                  {t('price-from-month', {
                                    price: convertToReadable(installmentPrice),
                                  })}
                                </div>
                              </div>
                            )}
                          <div className={styles.bottom}>
                            <div className={styles.prices}>
                              <div className={styles.newPrice}>
                                {convertToReadable(netPrice)} {t('price')}
                              </div>
                            </div>
                            <div className={styles.functions}>
                              {isVariantExist ? (
                                <Button
                                  type="button"
                                  className={styles.basket}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    router.push('/cart');
                                  }}
                                >
                                  <IconCartActive />
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  className={styles.basket}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addToCart(variantId, variant?.availability);
                                  }}
                                  disabled={loadingStates[variantId ?? '']}
                                >
                                  <IconAdd />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
          </div>
          <div className={styles.categoryBrand} ref={ref}>
            {loading ? (
              <>
                <h3 className={styles.title}>
                  {t('another-category-brand')}{' '}
                  <Skeleton className={styles.skeleton} width={120} />
                </h3>
                <div className={styles.wrapperCards}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={`skeleton-${index}`} className={styles.card}>
                      <Skeleton
                        className={styles.skeleton}
                        width={72}
                        height={72}
                      />
                      <h4 className={styles.titleMiniCard}>
                        <Skeleton className={styles.skeleton} width={100} />
                      </h4>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h3 className={styles.title}>
                  {t('another-category-brand')} «{brand}»
                </h3>
                <div className={styles.wrapperCards}>
                  {trasnlatedBrandCategories?.map(brand => (
                    <div
                      key={brand?.id}
                      className={styles.card}
                      onClick={() =>
                        router.push(
                          `/category/${decodeBase64Id(brand?.id)}-${brand?.slug}`
                        )
                      }
                    >
                      <div className={styles.photo}>
                        {brand?.icon && (
                          <Image
                            className={styles.image146396Icon}
                            alt={brand?.name}
                            src={`${config.publicApiUrl}${brand?.icon}`}
                            width={62}
                            height={54}
                          />
                        )}
                      </div>
                      <h4 className={styles.titleMiniCard}>{brand?.name}</h4>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReccomendSection;
